<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  attribute: {
    type: Object,
    required: true,
  },
  isEditingView: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update', 'delete']);

const { t } = useI18n();

const [showAttributeListDropdown, toggleAttributeListDropdown] = useToggle();

const selectedValues = ref([]);
const dropdownJustOpened = ref(false);

const isMultiList = computed(
  () => props.attribute.allowMultipleSelection === true
);

const normalizeValue = val => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  return val ? [val] : [];
};

watch(
  () => props.attribute.value,
  newVal => {
    selectedValues.value = normalizeValue(newVal);
  },
  { immediate: true }
);

const options = computed(() => {
  return (
    props.attribute.attributeValues?.map(v => ({ label: v, value: v })) || []
  );
});

const displayText = computed(() => {
  const val = props.attribute.value;
  if (!val || (Array.isArray(val) && val.length === 0)) {
    return t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.TRIGGER.SELECT');
  }
  if (Array.isArray(val)) {
    return val.join(', ');
  }
  return val;
});

// Multi-select menu items (checkmarks)
const attributeListMenuItems = computed(() => {
  const selected = new Set(selectedValues.value);
  return options.value.map(opt => ({
    ...opt,
    action: 'toggle',
    isSelected: selected.has(opt.value),
  }));
});

const handleAttributeAction = action => {
  if (isMultiList.value) {
    const current = new Set(selectedValues.value);
    if (current.has(action.value)) {
      current.delete(action.value);
    } else {
      current.add(action.value);
    }
    selectedValues.value = [...current];
  } else {
    selectedValues.value = [action.value];
    toggleAttributeListDropdown(false);
    const currentPropVal = normalizeValue(props.attribute.value);
    const newVal = action.value;
    if (String(newVal) !== String(currentPropVal[0] ?? '')) {
      emit('update', newVal);
    }
  }
};

const closeDropdown = () => {
  if (dropdownJustOpened.value) {
    dropdownJustOpened.value = false;
    return;
  }
  toggleAttributeListDropdown(false);

  if (!isMultiList.value) return;

  const currentPropVal = normalizeValue(props.attribute.value);
  const currentSelection = [...selectedValues.value].sort().join(',');
  const currentProp = [...currentPropVal].sort().join(',');
  if (currentSelection !== currentProp) {
    emit('update', [...selectedValues.value]);
  }
};

const openDropdown = () => {
  selectedValues.value = normalizeValue(props.attribute.value);
  dropdownJustOpened.value = true;
  toggleAttributeListDropdown(true);
};
</script>

<template>
  <div
    class="flex w-full min-w-0 gap-2"
    :class="{
      'items-start': isEditingView,
      'items-center justify-end': !isEditingView,
    }"
  >
    <!-- Editing view: multi-select pills -->
    <template v-if="isEditingView && isMultiList">
      <div v-on-clickaway="closeDropdown" class="relative flex-1 min-w-0">
        <div
          class="flex flex-wrap items-center gap-1 cursor-pointer min-w-0"
          @click="openDropdown"
        >
          <span
            v-for="val in selectedValues"
            :key="val"
            class="inline-flex items-center px-1.5 py-px text-xs rounded border border-n-strong/10 bg-n-alpha-1 text-n-slate-12 max-w-[180px] truncate"
          >
            {{ val }}
          </span>
          <span
            v-if="selectedValues.length === 0"
            class="text-sm text-n-slate-11"
          >
            {{ t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.TRIGGER.SELECT') }}
          </span>
        </div>

        <DropdownMenu
          v-if="showAttributeListDropdown"
          :menu-items="attributeListMenuItems"
          show-search
          class="w-48 mt-1 top-full ltr:left-0 rtl:right-0"
          @action="handleAttributeAction($event)"
        />
      </div>

      <div class="flex items-center gap-1 flex-shrink-0 pt-px">
        <Button
          variant="faded"
          color="slate"
          icon="i-lucide-pencil"
          size="xs"
          class="flex-shrink-0 opacity-0 group-hover/attribute:opacity-100 hover:no-underline"
          @click="openDropdown"
        />
        <Button
          variant="faded"
          color="ruby"
          icon="i-lucide-trash"
          size="xs"
          class="flex-shrink-0 opacity-0 group-hover/attribute:opacity-100 hover:no-underline"
          @click="emit('delete')"
        />
      </div>
    </template>

    <!-- Editing view: single-select dropdown -->
    <template v-else-if="isEditingView">
      <div v-on-clickaway="closeDropdown" class="relative flex items-center">
        <span
          class="min-w-0 text-sm text-n-slate-12 cursor-pointer flex-1 py-2"
          @click="openDropdown"
        >
          {{ displayText }}
        </span>
        <DropdownMenu
          v-if="showAttributeListDropdown"
          :menu-items="
            options.map(opt => ({
              ...opt,
              action: 'select',
              isSelected: String(props.attribute.value) === String(opt.value),
            }))
          "
          show-search
          class="w-48 mt-2 top-full ltr:left-0 rtl:right-0"
          @action="handleAttributeAction($event)"
        />
      </div>

      <div class="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="faded"
          color="slate"
          icon="i-lucide-pencil"
          size="xs"
          class="flex-shrink-0 opacity-0 group-hover/attribute:opacity-100 hover:no-underline"
          @click="openDropdown"
        />
        <Button
          variant="faded"
          color="ruby"
          icon="i-lucide-trash"
          size="xs"
          class="flex-shrink-0 opacity-0 group-hover/attribute:opacity-100 hover:no-underline"
          @click="emit('delete')"
        />
      </div>
    </template>

    <!-- Non-editing view: simple text -->
    <template v-else>
      <div v-on-clickaway="closeDropdown" class="relative flex items-center">
        <span
          class="min-w-0 text-sm cursor-pointer text-n-slate-11 hover:text-n-slate-12 py-2 select-none font-medium"
          @click="openDropdown"
        >
          {{ displayText }}
        </span>
        <DropdownMenu
          v-if="showAttributeListDropdown"
          :menu-items="
            isMultiList
              ? attributeListMenuItems
              : options.map(opt => ({
                  ...opt,
                  action: 'select',
                  isSelected:
                    String(props.attribute.value) === String(opt.value),
                }))
          "
          show-search
          class="w-48 mt-2 top-full ltr:right-0 rtl:left-0"
          @action="handleAttributeAction($event)"
        />
      </div>
    </template>
  </div>
</template>
